import { useEffect, useRef } from "react";
import { Button, Flex, QueryLoader, Text, toast } from "@jsq/ds/core";
import { Icon } from "@jsq/ds/icons";
import { Colors } from "@jsq/ds/styles";
import { useLatestRef } from "~/common/hooks/useLatestRef";
import { useInvestorWizard } from "../../context";
import { ProfileSnapshot } from "../ProfileSnapshot";
import { ProfileSelection } from "./ProfileSelection";
import * as styles from "./styles_vanilla.css";
import { useRankedProfiles } from "./useRankedProfiles";
import { CREATE_NEW_OPTION_ID, NONE_OF_THE_ABOVE_OPTION_ID } from "./utils";

const MISSING_GUID_MESSAGE =
  "This profile is missing an identifier and can't be used to create a case.";

interface Props {
  onBack: () => void;
  onCreateNew: () => void;
  onSelectExisting: (id: string, name: string, guid: string, isIaAssociated: boolean) => void;
  selectedId: string | null;
  onSelectedIdChange: (id: string | null) => void;
  searchValue: string;
  onSearchChange: (value: string) => void;
}

const SearchPrompt = () => (
  <Flex className={styles.previewPrompt}>
    <Flex className={styles.previewPromptHeader}>
      <Icon type="search" variant="outline" size="xxx-large" color={Colors.Neutral600} />
      <Text variant="h3" align="center" className={styles.previewPromptTitle}>
        Search and select a compliance profile
      </Text>
    </Flex>
    <Text variant="secondary-md" align="center">
      Find an existing profile or add a new one
    </Text>
  </Flex>
);

/** Selection and search state are owned by the parent so they survive navigation. */
export const ProfileIntro = ({
  onBack,
  onCreateNew,
  onSelectExisting,
  selectedId,
  onSelectedIdChange,
  searchValue,
  onSearchChange,
}: Props) => {
  const {
    setFooterContent,
    setIsFullWidth,
    caseInfo,
    profileSearchActive,
    setProfileSearchActive,
    selectedProfileName: contextSelectedProfileName,
    selectedProfileGuid: contextSelectedProfileGuid,
    selectedProfileIsIaAssociated: contextIsIaAssociated,
  } = useInvestorWizard();

  const {
    profiles,
    isSearchHidden,
    isSearching,
    extraOption,
    queryResults,
    arenaId,
    entityId,
    investmentAccountId,
    investmentAccountSettled,
    canReturnToAccountList,
  } = useRankedProfiles({
    caseInfo,
    searchValue,
    forceSearch: profileSearchActive,
  });

  // Captures the chosen profile's identity so it survives the search-filtered list changing under
  // it. Seeded from context so a selection restored on navigation is usable before results reload.
  const selectedDetailsRef = useRef<{
    id: string;
    name: string;
    guid: string | null;
    isIaAssociated: boolean;
  } | null>(
    selectedId !== null &&
      selectedId !== CREATE_NEW_OPTION_ID &&
      contextSelectedProfileName !== null
      ? {
          id: selectedId,
          name: contextSelectedProfileName,
          guid: contextSelectedProfileGuid,
          isIaAssociated: contextIsIaAssociated,
        }
      : null,
  );

  const hasSelection = selectedId !== null;

  // "None of the above" opts out of the investment-account list into free search, confirmed
  // (like any other choice) by clicking Continue rather than acting the moment it's selected.
  const handleContinue = () => {
    if (selectedId === null) return;
    if (selectedId === NONE_OF_THE_ABOVE_OPTION_ID) {
      setProfileSearchActive(true);
      selectedDetailsRef.current = null;
      onSelectedIdChange(null);
      onSearchChange("");
      return;
    }
    if (selectedId === CREATE_NEW_OPTION_ID) {
      onCreateNew();
      return;
    }
    // The selected profile may have dropped out of the current search results.
    const listed = profiles.find((profile) => profile.id === selectedId);
    const captured =
      selectedDetailsRef.current?.id === selectedId ? selectedDetailsRef.current : null;
    const selected = listed ?? captured;
    if (!selected) {
      onSelectedIdChange(null);
      return;
    }
    if (selected.guid === null) {
      toast.error(MISSING_GUID_MESSAGE);
      onSelectedIdChange(null);
      return;
    }
    const isIaAssociated =
      listed !== undefined
        ? investmentAccountId !== null &&
          listed.associatedInvestmentAccountIds.includes(investmentAccountId)
        : (captured?.isIaAssociated ?? false);
    onSelectExisting(selectedId, selected.name, selected.guid, isIaAssociated);
  };

  // Search reached from a list of suggestions returns to that list first; only when there was
  // never one (no IA, or an IA with no profiles) does Back leave the step.
  const handleBack = () => {
    if (profileSearchActive && canReturnToAccountList) {
      setProfileSearchActive(false);
      return;
    }
    onBack();
  };

  // The footer lives in the wizard shell; reading the handlers through a ref lets the
  // registering effect below run only once per `hasSelection` change, instead of on every
  // render of the parent's non-memoized callbacks.
  const handlersRef = useLatestRef({ handleContinue, handleBack });

  useEffect(() => {
    setIsFullWidth(true);
    setFooterContent(
      <>
        <Button variant="neutral" onClick={() => handlersRef.current.handleBack()}>
          Back
        </Button>
        <Button
          variant="primary"
          onClick={() => handlersRef.current.handleContinue()}
          disabled={!hasSelection}
        >
          Continue
        </Button>
      </>,
    );
    return () => {
      setIsFullWidth(false);
      setFooterContent(null);
    };
  }, [setFooterContent, setIsFullWidth, hasSelection, handlersRef]);

  const handleSelect = (id: string) => {
    if (id === NONE_OF_THE_ABOVE_OPTION_ID) {
      onSelectedIdChange(id);
      return;
    }
    const profile = profiles.find((item) => item.id === id);
    if (profile) {
      if (profile.guid === null) {
        toast.error(MISSING_GUID_MESSAGE);
        return;
      }
      selectedDetailsRef.current = {
        id,
        name: profile.name,
        guid: profile.guid,
        isIaAssociated:
          investmentAccountId !== null &&
          profile.associatedInvestmentAccountIds.includes(investmentAccountId),
      };
    }
    onSelectedIdChange(id);
  };

  // Both synthetic options can sit in `selectedId` while awaiting Continue, and neither has a
  // profile of its own to show in the snapshot panel.
  const isExistingProfileSelected =
    hasSelection &&
    selectedId !== CREATE_NEW_OPTION_ID &&
    selectedId !== NONE_OF_THE_ABOVE_OPTION_ID;

  const selectedListItem = profiles.find((item) => item.id === selectedId);
  const selectedProfileName =
    selectedListItem?.name ?? selectedDetailsRef.current?.name ?? contextSelectedProfileName;
  const selectedProfileGuid =
    selectedListItem?.guid ?? selectedDetailsRef.current?.guid ?? contextSelectedProfileGuid;
  const selectedIsIaAssociated = selectedListItem
    ? investmentAccountId !== null &&
      selectedListItem.associatedInvestmentAccountIds.includes(investmentAccountId)
    : (selectedDetailsRef.current?.isIaAssociated ?? contextIsIaAssociated);

  return (
    <QueryLoader result={queryResults}>
      <Flex className={styles.container}>
        <Flex className={styles.leftPanel}>
          <ProfileSelection
            profiles={profiles}
            selectedId={selectedId}
            searchValue={searchValue}
            onSearchChange={onSearchChange}
            onSelect={handleSelect}
            onBack={onBack}
            hideSearch={isSearchHidden}
            isSearching={isSearching}
            extraOption={extraOption}
          />
        </Flex>
        <Flex className={styles.rightPanel}>
          {isExistingProfileSelected ? (
            <ProfileSnapshot
              rootProfileId={Number(selectedId)}
              arenaId={arenaId}
              entityId={entityId}
              investmentAccountId={investmentAccountId}
              investmentAccountSettled={investmentAccountSettled}
              profileGuid={selectedProfileGuid}
              profileName={selectedProfileName}
              isIaAssociated={selectedIsIaAssociated}
            />
          ) : (
            <SearchPrompt />
          )}
        </Flex>
      </Flex>
    </QueryLoader>
  );
};
